import React, { FC, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Router, useHistory, useLocation } from 'react-router';
import { createBrowserHistory } from 'history';

import { CssBaseline } from '@material-ui/core';
import { jssPreset, MuiThemeProvider, StylesProvider } from '@material-ui/core/styles';
import { create } from 'jss';
import jssPluginIsolate from 'jss-plugin-isolate';

import { CognitoUserProvider, NotifyProvider } from 'providers';
import { Queries, ScrollToTop } from 'components';
import storage from 'storage';

import { GetActiveClientByAttrQueryVariables, UniqueClientKey } from 'api/public';
import { getSubdomainUrl } from 'helpers';
import { isRoot } from 'utilities';
import { RootClientSelect } from 'features/auth';
import i18n from 'config/i18n';
import theme from 'theme';
import * as paths from 'paths';

import Protected from './protected/ProtectedIndex';
import Public from './public/PublicIndex';
import Connections from './protected/Connections';
import Routes from './protected/Routes';
import ReactQueryProvider from './ReactQueryProvider';

const jss = create({
  plugins: [...jssPreset().plugins, jssPluginIsolate()],
});

const Subdomain: FC = () => {
  const location = useLocation();
  const history = useHistory();

  const isUnsubscribing = location.pathname === paths.unsubscribe;

  useEffect(() => {
    window?.analytics?.page();
    history.listen(() => window?.analytics?.page());
  }, []);

  function renderProtected() {
    if (isUnsubscribing) {
      return <Public />;
    }

    return (
      <Protected>
        <Connections />
        <Routes />
      </Protected>
    );
  }

  return (
    <CognitoUserProvider>
      {cognitoState => (cognitoState ? renderProtected() : <Public />)}
    </CognitoUserProvider>
  );
};

const RootDomain: FC = () => {
  const params = new URLSearchParams(window.location.search);
  const isSignedInAndVisitingRoot = storage.clientSubdomain;
  const isSignedInWithSSOAndVisitingRoot = params.get('provider');

  useEffect(() => {
    window?.analytics?.page();
  }, []);

  if (!isSignedInWithSSOAndVisitingRoot && !isSignedInAndVisitingRoot) {
    return <RootClientSelect />;
  }

  let variables = {} as GetActiveClientByAttrQueryVariables;

  if (isSignedInAndVisitingRoot) {
    variables = {
      key: UniqueClientKey.Subdomain,
      value: storage.clientSubdomain,
    };
  }

  if (isSignedInWithSSOAndVisitingRoot) {
    variables = {
      key: UniqueClientKey.Provider,
      value: params.get('provider'),
    };
  }

  return (
    <Queries.GetActiveClientByAttr variables={variables}>
      {client => {
        const subdomainUrl = getSubdomainUrl(client.subdomain);
        window.location.assign(subdomainUrl + location.pathname + location.search);
        return null;
      }}
    </Queries.GetActiveClientByAttr>
  );
};

const App: FC = () => (
  <StylesProvider jss={jss}>
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <I18nextProvider i18n={i18n}>
        <Router history={createBrowserHistory()}>
          <ScrollToTop />
          <ReactQueryProvider>
            <NotifyProvider>{isRoot() ? <RootDomain /> : <Subdomain />}</NotifyProvider>
          </ReactQueryProvider>
        </Router>
      </I18nextProvider>
    </MuiThemeProvider>
  </StylesProvider>
);

export default App;
